export type FirstYardHudInputMode = "keyboard" | "pointer" | "touch";
export type FirstYardHudFacing = number | Readonly<{ readonly x: number; readonly z: number }>;

export interface FirstYardHudSnapshot {
  readonly position: Readonly<{
    readonly x: number;
    readonly z: number;
  }>;
  readonly facing: FirstYardHudFacing;
  readonly moving: boolean;
  readonly objectiveId: string;
  readonly cargo: Readonly<{
    readonly kind: string | null;
    readonly count: number;
    readonly capacity: number;
  }>;
  readonly cash: number;
}

export interface FirstYardHudController {
  readonly root: HTMLElement;
  readonly joystick: HTMLButtonElement;
  readonly work: HTMLButtonElement;
  setSnapshot(snapshot: Readonly<FirstYardHudSnapshot>): void;
  setInputMode(mode: FirstYardHudInputMode): void;
  setReducedMotion(reduced: boolean): void;
  dispose(): void;
}

export const FIRST_YARD_HUD_EN = Object.freeze({
  "hud.region.label": "Farm status",
  "hud.world_cue": "Nan's Town · First Yard",
  "hud.cash.label": "Cash",
  "hud.objective.explore": "Explore the first yard",
  "hud.objective.visit_field": "Visit the corn row",
  "hud.objective.visit_pen": "Visit the chicken pen",
  "hud.objective.visit_market": "Visit the roadside market",
  "hud.objective.visit_gate": "Visit the north gate",
  "hud.objective.pick_corn": "Pick 4 corn",
  "hud.objective.feed_chickens": "Feed chickens",
  "hud.objective.collect_eggs": "Collect eggs",
  "hud.objective.sell_eggs": "Sell eggs",
  "hud.objective.pen_extension_ready": "Pen Extension available · 24 Cash",
  "hud.carry.label": "Carrying",
  "hud.keyboard_hint": "WASD/arrows move · stand still to work · Space optional",
  "hud.joystick.label": "Move farmer",
  "hud.work.label": "Work",
} as const);

type HudStringKey = keyof typeof FIRST_YARD_HUD_EN;

interface ObjectivePresentation {
  readonly key: HudStringKey;
  readonly icon: "chicken" | "corn" | "egg" | "gate" | "market" | "sun-stitch";
}

const OBJECTIVES: Readonly<Record<string, ObjectivePresentation>> = Object.freeze({
  explore: Object.freeze({ key: "hud.objective.explore", icon: "sun-stitch" }),
  "explore-yard": Object.freeze({ key: "hud.objective.explore", icon: "sun-stitch" }),
  "visit-field": Object.freeze({ key: "hud.objective.visit_field", icon: "corn" }),
  "visit-pen": Object.freeze({ key: "hud.objective.visit_pen", icon: "corn" }),
  "visit-market": Object.freeze({ key: "hud.objective.visit_market", icon: "market" }),
  "visit-gate": Object.freeze({ key: "hud.objective.visit_gate", icon: "gate" }),
  "locked-gate": Object.freeze({ key: "hud.objective.visit_gate", icon: "gate" }),
  "pick-corn": Object.freeze({ key: "hud.objective.pick_corn", icon: "corn" }),
  "feed-chickens": Object.freeze({ key: "hud.objective.feed_chickens", icon: "chicken" }),
  "collect-eggs": Object.freeze({ key: "hud.objective.collect_eggs", icon: "egg" }),
  "sell-eggs": Object.freeze({ key: "hud.objective.sell_eggs", icon: "market" }),
  "pen-extension-ready": Object.freeze({ key: "hud.objective.pen_extension_ready", icon: "gate" }),
});

const ITEM_KINDS = new Set(["cash", "corn", "egg", "none"]);

export function createFirstYardHud(app: HTMLElement): FirstYardHudController {
  const document = app.ownerDocument;
  const root = element(document, "section", "first-yard-hud");
  root.id = "first-yard-hud";
  localizeAttribute(root, "aria-label", "hud.region.label");
  root.dataset.layout = "responsive";
  root.dataset.fontFamily = "atkinson-hyperlegible";
  root.dataset.safeArea = "viewport";
  root.dataset.textScale = "200-percent-ready";
  root.dataset.inputMode = "keyboard";
  root.dataset.reducedMotion = "false";
  root.dataset.moving = "false";
  root.dataset.cargoState = "empty";
  root.dataset.objective = "explore";
  root.style.fontFamily = '"Atkinson Hyperlegible", system-ui, sans-serif';

  const worldCue = localizedText(document, "p", "first-yard-hud__world-cue", "hud.world_cue");

  const cash = element(document, "div", "first-yard-hud__cash");
  cash.id = "first-yard-cash";
  cash.setAttribute("role", "group");
  localizeAttribute(cash, "aria-label", "hud.cash.label");
  cash.dataset.hudRegion = "cash";
  cash.dataset.surface = "notched-seed-tag";
  const cashIcon = icon(document, "first-yard-hud__cash-icon", "cash-bundle");
  const cashValue = localizedText(document, "span", "first-yard-hud__cash-value", "hud.cash.label", "0");
  cashValue.dataset.numeric = "tabular";
  cash.append(cashIcon, cashValue);

  const objective = element(document, "div", "first-yard-hud__objective");
  objective.id = "first-yard-objective";
  objective.setAttribute("role", "group");
  objective.dataset.hudRegion = "objective";
  objective.dataset.surface = "notched-seed-tag";
  const objectiveIcon = icon(document, "first-yard-hud__objective-icon", "sun-stitch");
  const objectiveLabel = localizedText(
    document,
    "span",
    "first-yard-hud__objective-label",
    "hud.objective.explore",
  );
  objective.setAttribute("aria-label", objectiveLabel.textContent ?? "");
  objective.setAttribute("data-l10n-key", "hud.objective.explore");
  objective.append(objectiveIcon, objectiveLabel);

  const carry = element(document, "div", "first-yard-hud__carry");
  carry.id = "first-yard-carry";
  carry.setAttribute("role", "group");
  localizeAttribute(carry, "aria-label", "hud.carry.label");
  carry.dataset.hudRegion = "carry";
  carry.dataset.surface = "notched-seed-tag";
  const carryIcon = icon(document, "first-yard-hud__carry-icon", "empty-basket");
  carryIcon.dataset.itemKind = "none";
  const carryValue = localizedText(document, "span", "first-yard-hud__carry-value", "hud.carry.label", "0/0");
  carryValue.dataset.numeric = "tabular";
  carry.append(carryIcon, carryValue);

  const keyboardHint = localizedText(
    document,
    "p",
    "first-yard-hud__keyboard-hint",
    "hud.keyboard_hint",
  );
  keyboardHint.id = "first-yard-keyboard-hint";
  keyboardHint.dataset.hudRegion = "keyboard-hint";

  const joystick = element(document, "button", "first-yard-hud__joystick") as HTMLButtonElement;
  joystick.id = "first-yard-joystick";
  joystick.setAttribute("type", "button");
  joystick.setAttribute("aria-keyshortcuts", "W A S D ArrowUp ArrowDown ArrowLeft ArrowRight");
  localizeAttribute(joystick, "aria-label", "hud.joystick.label");
  joystick.dataset.control = "dynamic-joystick";
  joystick.dataset.focusRing = "teal-double";
  joystick.dataset.hudRegion = "movement";
  joystick.dataset.minTargetCssPx = "48";
  joystick.dataset.active = "false";
  joystick.dataset.axisX = "0";
  joystick.dataset.axisY = "0";
  const joystickRing = icon(document, "first-yard-hud__joystick-ring", "woven-ring");
  const joystickKnob = icon(document, "first-yard-hud__joystick-knob", "joystick-knob");
  joystick.append(joystickRing, joystickKnob);

  const work = element(document, "button", "first-yard-hud__work") as HTMLButtonElement;
  work.id = "first-yard-work";
  work.setAttribute("type", "button");
  work.setAttribute("aria-keyshortcuts", "Space Enter KeyE");
  localizeAttribute(work, "aria-label", "hud.work.label");
  work.dataset.control = "work-action";
  work.dataset.focusRing = "gold-double";
  work.dataset.hudRegion = "action";
  work.dataset.minTargetCssPx = "48";
  work.dataset.active = "false";
  const workIcon = icon(document, "first-yard-hud__work-icon", "glove");
  const workLabel = localizedText(document, "span", "first-yard-hud__work-label", "hud.work.label");
  work.append(workIcon, workLabel);

  const live = element(document, "div", "first-yard-hud__live");
  live.id = "first-yard-hud-status";
  live.classList.add("first-yard-hud__sr-only");
  live.setAttribute("role", "status");
  live.setAttribute("aria-live", "polite");
  live.setAttribute("aria-atomic", "true");

  root.append(worldCue, cash, objective, carry, keyboardHint, joystick, work, live);
  app.append(root);

  let disposed = false;
  let lastObjectiveKey: HudStringKey | null = null;

  const setInputMode = (mode: FirstYardHudInputMode): void => {
    if (disposed) return;
    root.dataset.inputMode = mode;
    const touch = mode === "touch";
    joystick.hidden = !touch;
    joystick.tabIndex = touch ? 0 : -1;
    joystick.setAttribute("aria-hidden", String(!touch));
    keyboardHint.hidden = touch;
    keyboardHint.setAttribute("aria-hidden", String(touch));
    work.hidden = !touch;
    work.tabIndex = touch ? 0 : -1;
    work.setAttribute("aria-hidden", String(!touch));
  };

  setInputMode("keyboard");

  return Object.freeze({
    root,
    joystick,
    work,
    setSnapshot(snapshot: Readonly<FirstYardHudSnapshot>): void {
      if (disposed) return;
      const objectiveState = objectiveFor(snapshot.objectiveId);
      const objectiveText = FIRST_YARD_HUD_EN[objectiveState.key];
      const count = nonNegativeInteger(snapshot.cargo.count);
      const capacity = nonNegativeInteger(snapshot.cargo.capacity);
      const itemKind = normalizedItemKind(snapshot.cargo.kind);
      const hasCargo = count > 0;

      cashValue.textContent = String(nonNegativeInteger(snapshot.cash));
      objectiveLabel.textContent = objectiveText;
      objectiveLabel.setAttribute("data-l10n-key", objectiveState.key);
      objectiveIcon.dataset.icon = objectiveState.icon;
      objective.setAttribute("aria-label", objectiveText);
      objective.setAttribute("data-l10n-key", objectiveState.key);
      carryValue.textContent = `${count}/${capacity}`;
      carryIcon.dataset.itemKind = itemKind;
      carryIcon.dataset.icon = itemKind === "none" ? "empty-basket" : itemKind;

      root.dataset.moving = String(snapshot.moving);
      root.dataset.cargoState = hasCargo ? "carrying" : "empty";
      root.dataset.objective = knownObjectiveId(snapshot.objectiveId);
      root.classList.toggle("is-moving", snapshot.moving);
      root.classList.toggle("has-cargo", hasCargo);

      if (lastObjectiveKey !== objectiveState.key) {
        live.textContent = objectiveText;
        live.setAttribute("data-l10n-key", objectiveState.key);
        lastObjectiveKey = objectiveState.key;
      }
    },
    setInputMode,
    setReducedMotion(reduced: boolean): void {
      if (disposed) return;
      root.dataset.reducedMotion = String(reduced);
      root.classList.toggle("is-reduced-motion", reduced);
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      root.remove();
    },
  });
}

function element<K extends keyof HTMLElementTagNameMap>(
  document: Document,
  tagName: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tagName);
  node.classList.add(className);
  return node;
}

function localizedText<K extends keyof HTMLElementTagNameMap>(
  document: Document,
  tagName: K,
  className: string,
  key: HudStringKey,
  text: string = FIRST_YARD_HUD_EN[key],
): HTMLElementTagNameMap[K] {
  const node = element(document, tagName, className);
  node.textContent = text;
  node.setAttribute("data-l10n-key", key);
  return node;
}

function localizeAttribute(node: HTMLElement, attribute: string, key: HudStringKey): void {
  node.setAttribute(attribute, FIRST_YARD_HUD_EN[key]);
  node.setAttribute("data-l10n-key", key);
}

function icon(document: Document, className: string, name: string): HTMLSpanElement {
  const node = element(document, "span", className);
  node.setAttribute("aria-hidden", "true");
  node.dataset.icon = name;
  return node;
}

function objectiveFor(id: string): ObjectivePresentation {
  return OBJECTIVES[id] ?? OBJECTIVES.explore!;
}

function knownObjectiveId(id: string): string {
  return OBJECTIVES[id] === undefined ? "explore" : id;
}

function normalizedItemKind(kind: string | null): string {
  if (kind === null || kind === "" || kind === "none") return "none";
  return ITEM_KINDS.has(kind) ? kind : "unknown";
}

function nonNegativeInteger(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
}
